import { useState } from 'react';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';
import axiosInstance from 'interceptor/axiosInstance';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { deserialize, serialize } from 'serializr';
import { META_TYPE } from 'constants/FarmboxConstants/stage0.constants';
import { RawMaterialIngredientsMeta } from 'models/RawMaterialIngredientsMeta/rawMaterialIngredientsMeta.model';
import {
  FarmboxRawMaterials,
  FarmboxRawMaterialsData,
} from 'models/FarmboxRawMaterials/farmBoxRawMaterials.model';
import { generatePath } from 'react-router-dom';
import { generateAddressLabel } from 'views/Scope3/Category4&9/LocationModal';

const FarmboxRawMaterialsService = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [draftButtonLoading, setDraftButtonLoading] = useState<boolean>(false);
  const [rawMaterials, setRawMaterials] = useState<FarmboxRawMaterialsData>();
  const [rawMaterialFertilisersMeta, setRawMaterialFertilisersMeta] = useState<
    RawMaterialIngredientsMeta[]
  >([]);

  const addAddressLabel = (items?: FarmboxRawMaterials[]) =>
    items?.map(item => ({
      ...item,
      addressLabel: item?.address?.country
        ? generateAddressLabel(item?.address)
        : undefined,
      origins: item?.multiOrigin
        ? item?.origins?.map(origin => ({
            ...origin,
            ...(origin?.address && {
              addressLabel: generateAddressLabel(origin.address),
            }),
          }))
        : [],
    }));

  const updateRawMaterials = async (
    commodityId: number,
    payload: FarmboxRawMaterialsData,
    isDraft?: boolean
  ) => {
    try {
      isDraft ? setDraftButtonLoading(true) : setButtonLoading(true);

      const desiredPayload = serialize(FarmboxRawMaterialsData, payload);
      const url = generatePath(ApiRoutes.FARMBOX_RAW_MATERIALS, {
        commodityId,
      });
      await axiosInstance.put(url, desiredPayload, {
        params: { draft: isDraft },
      });
      Notification({
        message: 'Raw Materials Updated Successfully',
        description: '',
        type: NotificationTypes.SUCCESS,
      });
      return true;
    } catch (error) {
      return false;
    } finally {
      isDraft ? setDraftButtonLoading(false) : setButtonLoading(false);
    }
  };
  const getRawMaterials = async (commodityId: number) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.FARMBOX_RAW_MATERIALS, {
        commodityId,
      });
      const response = await axiosInstance.get(url);
      const desiredPayload = deserialize(
        FarmboxRawMaterialsData,
        response.data
      );
      const modifiedCategoryData: FarmboxRawMaterialsData = {
        ...desiredPayload,
        fertilizers: addAddressLabel(desiredPayload?.fertilizers),
        packagings: addAddressLabel(desiredPayload?.packagings),
      };
      setRawMaterials(modifiedCategoryData);
      return modifiedCategoryData;
    } finally {
      setLoading(false);
    }
  };
  const fetchMetaData = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(ApiRoutes.META_INGREDIENTS_STAGE_1, {
        params: { type: META_TYPE },
      });
      const desiredMetaData = deserialize(
        RawMaterialIngredientsMeta,
        res.data
      ) as unknown as RawMaterialIngredientsMeta[];
      setRawMaterialFertilisersMeta(desiredMetaData);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };
  return {
    loading,
    buttonLoading,
    draftButtonLoading,
    updateRawMaterials,
    fetchMetaData,
    rawMaterialFertilisersMeta,
    getRawMaterials,
    rawMaterials,
  };
};

export default FarmboxRawMaterialsService;
